import React, { useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import Button from "../../Button/Button";
import Modal from "../Modal";
import { AddUserProfileImage } from "../../../services/external.api.service";
import useAuthService from "../../../auth/AuthService";
import imageCompression from "browser-image-compression";
import ReactCrop, { Crop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

interface FileUploaderProps {
  isVisible?: boolean;
  onClose?: () => void;
  onClick?: () => void;
  handleImageChange: (image_url: string) => void;
}

const FileUploader: React.FC<FileUploaderProps> = ({
  isVisible,
  onClose,
  onClick,
  handleImageChange,
}) => {
  const hiddenInputRef = useRef<HTMLInputElement | null>(null);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [crop, setCrop] = useState<Crop>({
    unit: '%',
    width: 100,
    height: 100,
    x: 0, y: 0
  });
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const [croppedImageUrl, setCroppedImageUrl] = useState<string | null>(null);

  const { getToken } = useAuthService();

  const { getRootProps, getInputProps, open } = useDropzone({
    accept: { "image/*": [".jpeg", ".jpg", ".png", ".webp"] },
    multiple: false,
    onDrop: async (incomingFiles) => {
      const file = incomingFiles[0];
      if (file) {
        const type = file.type;
        if (
          type === "image/jpeg" ||
          type === "image/png" ||
          type === "image/webp" ||
          type === "image/jpg"
        ) {
          if (hiddenInputRef.current) {
            const dataTransfer = new DataTransfer();
            dataTransfer.items.add(file);
            hiddenInputRef.current.files = dataTransfer.files;
          }
          const compressfile = await compressImage(file);
          setImageSrc(URL.createObjectURL(compressfile));
        } else {
          alert("Only JPEG, PNG, or WebP images are allowed.");
        }
      }
    },
    noClick: true,
    noKeyboard: true,
  });

  const compressImage = async (file: File): Promise<File> => {
    const options = {
      maxSizeMB: 0.5,
      maxWidthOrHeight: 200,
      useWebWorker: true,
    };
    try {
      const compressedFile = await imageCompression(file, options);
      return compressedFile;
    } catch (error) {
      console.error("Error during image compression:", error);
      return file;
    }
  };

  const onImageLoaded = (image: HTMLImageElement) => {
    setImageSrc(image.src); // save the loaded image
    return false; // prevents automatic crop
  };


  const onCropChange = (newCrop: Crop) => {
    setCrop(newCrop);
  };

  const onCropComplete = async (crop: Crop) => {
    if (crop.width && crop.height) {
      const image = document.getElementById('image-to-crop') as HTMLImageElement;
      const canvas = document.createElement('canvas');
      const scaleX = image.naturalWidth / image.width;
      const scaleY = image.naturalHeight / image.height;
      canvas.width = crop.width;
      canvas.height = crop.height;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(
          image,
          crop.x * scaleX,
          crop.y * scaleY,
          crop.width * scaleX,
          crop.height * scaleY,
          0,
          0,
          crop.width,
          crop.height
        );
        const croppedImageUrl = canvas.toDataURL('image/jpeg');
        setCroppedImageUrl(croppedImageUrl); // This is your cropped image URL
      }
    }
  };

  const handleFileUpload = async (file: File) => {
    setIsUploading(true);
    const formData = new FormData();
    formData.append("image", file);
    try {
      const response = await AddUserProfileImage(formData, getToken);
      if (response) {
        handleImageChange(response.data.url);
        onClick && onClick();
      }
    } catch (error) {
      console.error("Error uploading file:", error);
      alert("Error uploading file!");
      onClose && onClose();
    } finally {
      setIsUploading(false);
    }
  };



  const handleBackgroundClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose && onClose();
    }
  };

  const filePreview = hiddenInputRef.current?.files?.[0] && (
    <div>
      <h5 className="text-light-purple mt-3">Uploaded File:</h5>
      <ul>
        <li>
          <ReactCrop
            crop={crop}
            onChange={onCropChange}
            onComplete={onCropComplete}
            aspect={1}
              
          >
            <img
              id="image-to-crop"
              src={imageSrc || URL.createObjectURL(hiddenInputRef.current.files[0])}
              onLoad={(e) => onImageLoaded(e.currentTarget)}
              alt={hiddenInputRef.current.files[0].name}
              style={{ maxWidth: '100%', maxHeight: 300 }}
            />
          </ReactCrop>
        </li>
      </ul>
    </div>
  );

  const modalBody = (
    <div className="p-5 bg-midnight rounded-3">
      <div className="file-upload">
        <div {...getRootProps({ className: "dropzone" })}>
          <input
            type="file"
            name="file"
            required
            style={{ opacity: 0, position: "absolute", pointerEvents: "none" }}
            ref={hiddenInputRef}
          />
          <input {...getInputProps()} />
          <p className="font-500-20 text-light-purple py-3">
            Drag and drop to upload an image
          </p>
          {!filePreview && <Button
            label={
              <>
                Browse Files <i className="fa-solid fa-cloud-arrow-up ms-2"></i>
              </>
            }
            onClick={open}
            className="py-2 font-500-15"
          />}
        </div>
        {filePreview &&
          <>{filePreview}
            <Button
              label="Save"
              className="py-2 font-500-15"
              onClick={async () => {
                if (croppedImageUrl) {
                  const blob = await fetch(croppedImageUrl).then((res) => res.blob());
                  const croppedFile = new File([blob], "cropped-image.jpg", {
                    type: "image/jpeg",
                  });
                  await handleFileUpload(croppedFile);
                }
              }}
            /></>}
        {isUploading && (
          <div className="loader">
            <div className="spinner"></div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <Modal
      modalClassName="file-modal"
      id="FileUploader"
      body={modalBody}
      modalBackground="modal-bg"
      onClick={handleBackgroundClick}
      isVisible={isVisible}
    />
  );
};

export default FileUploader;
